import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import type {
  ProjectFunctionSeries,
  ProjectFunctionDay,
} from "@/hooks/useProjectPeople";

export interface FunctionOption {
  series: ProjectFunctionSeries;
  day: ProjectFunctionDay;
  hasOpenSpots: boolean;
  currentCount: number;
  requiredCount: number;
}

interface SelectFunctionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  personName: string;
  functionOptions: FunctionOption[];
  onSelectFunction: (dayId: string, incrementCapacity: boolean) => void;
  onCreateFunction: () => void;
  isPending: boolean;
}

export function SelectFunctionDialog({
  open,
  onOpenChange,
  personName,
  functionOptions,
  onSelectFunction,
  onCreateFunction,
  isPending,
}: SelectFunctionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="select-function-dialog">
        <DialogHeader>
          <DialogTitle>Assign {personName}</DialogTitle>
          <DialogDescription>
            {functionOptions.length > 0
              ? "Select a camera position function to assign this person to."
              : "No camera position functions exist for this date."}
          </DialogDescription>
        </DialogHeader>

        {functionOptions.length > 0 ? (
          <div className="space-y-2">
            {functionOptions.map(
              ({ series, day, hasOpenSpots, currentCount, requiredCount }) => (
                <div
                  key={day.id}
                  className="flex items-center justify-between rounded-md border p-3"
                  data-testid={`select-function-option-${day.id}`}
                >
                  <div>
                    <div className="text-sm font-medium">{series.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {currentCount}/{requiredCount} assigned
                    </div>
                  </div>
                  {hasOpenSpots ? (
                    <Button
                      size="sm"
                      disabled={isPending}
                      onClick={() => onSelectFunction(day.id, false)}
                    >
                      Select
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isPending}
                      onClick={() => onSelectFunction(day.id, true)}
                    >
                      Add spot & assign
                    </Button>
                  )}
                </div>
              ),
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <Button
              className="w-full"
              disabled={isPending}
              onClick={onCreateFunction}
            >
              Create Camera Position
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
